/**
 * Minimal API client.
 *
 * `credentials: 'include'` is mandatory: the session is a host-only cookie, so
 * omitting it would silently make every request anonymous.
 */
/**
 * Base URL for API calls.
 *
 * Empty in development: calls stay relative (`/api/...`) and the Vite dev proxy
 * forwards them to 127.0.0.1:3000. That keeps the host-only session cookie
 * working, because the browser sees a same-origin request.
 *
 * Empty in production too, by the same reasoning. The registered OAuth callback
 * URIs are per *app* origin (`{app}/api/auth/callback/google`), so sign-in has to
 * begin and end on the app origin. Both deployed apps therefore reverse-proxy
 * `/api` to the API and keep the session cookie host-only to the app origin.
 * Setting `VITE_API_BASE_URL` to the API origin would send `/api/auth/oauth/*`
 * to a host whose callback URI is not registered, and the gateway would reject
 * the sign-in. It stays unset by design; never bake in a default, because a wrong
 * default silently sends authenticated requests to the wrong host.
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * A closed-set code from the server, never prose. `declined_by_risk_policy`
   * is the one the checkout cares about today.
   */
  readonly reason?: string;

  constructor(status: number, code: string, message: string, reason?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.reason = reason;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Extra request headers.
   *
   * Exists for `X-Guest-Token` and nothing else: a guest donor's only
   * credential is the server-issued token bound to their donation, so the
   * payment steps have to carry it on the request itself.
   */
  headers?: Record<string, string>;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: {
      accept: 'application/json',
      ...(options.headers ?? {}),
      ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const envelope = payload as {
      error?: { code?: string; message?: string; reason?: string };
    } | null;
    throw new ApiRequestError(
      response.status,
      envelope?.error?.code ?? 'unknown_error',
      envelope?.error?.message ?? response.statusText,
      // The closed-set reason that sits beside the code, when there is one. It
      // is what lets a screen distinguish "this card was declined" from "this
      // order was released", which are different things to a donor and want
      // different words.
      envelope?.error?.reason,
    );
  }

  return payload as T;
}
